import React from "react";
import { VendorAddress } from "src/types/database/vendor";
import { TextInputBox } from "src/components/ui/inputs";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface Props {
  address: VendorAddress;
  index: number;
  addressesLength: number;
  getAddressState: (index: number) => boolean;
  onToggle: (index: number, isOpen: boolean) => void;
  onRemove: (index: number) => void;
  onSetCurrent: (index: number) => void;
  onChange: (index: number, field: keyof VendorAddress, value: any) => void;
  getFieldErrorMessage: (fieldName: string) => string | undefined;
}

const AddressItem: React.FC<Props> = ({
  address,
  index,
  addressesLength,
  getAddressState,
  onToggle,
  onRemove,
  onSetCurrent,
  onChange,
  getFieldErrorMessage
}) => {
  return (
    <CollapsibleSection
      key={index}
      title={
        <div className="flex items-center justify-between w-full min-w-0">
          <div className="flex items-center space-x-3 min-w-0">
            <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-800 rounded-full flex items-center justify-center text-sm font-semibold">
              {index + 1}
            </span>
            <span className="text-sm text-secondary-700 truncate">
              {address.line1 || "New Address"}
              {address.city && `, ${address.city}`}
              {address.zipCode && ` ${address.zipCode}`}
            </span>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
            {address.isCurrent && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                Current
              </span>
            )}
            {addressesLength > 1 && (
              <button
                type="button"
                className="inline-flex items-center p-1.5 text-error-600 hover:text-error-800 hover:bg-error-50 rounded-md transition-colors duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(index);
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      }
      isOpen={getAddressState(index)}
      onToggle={(isOpen) => onToggle(index, isOpen)}
      variant="minimal"
    >
      <div className="p-6 bg-secondary-50">
        <div className="mb-4">
          <label className="flex items-center space-x-2 text-sm font-medium text-secondary-700">
            <input
              type="radio"
              name="currentAddress"
              checked={address.isCurrent}
              onChange={() => onSetCurrent(index)}
              className="w-4 h-4 text-primary-600 border-secondary-300 focus:ring-primary-500"
            />
            <span>Set as Current Address</span>
          </label>
        </div>

        <div className="space-y-4">
          <div>
            <TextInputBox
              id={`address-${index}-line1`}
              label="Address Line 1"
              value={address.line1 || ""}
              onChange={(value: string) => onChange(index, "line1", value)}
              error={getFieldErrorMessage(`address_${index}_line1`)}
            />
          </div>

          <div>
            <TextInputBox
              id={`address-${index}-line2`}
              label="Address Line 2"
              value={address.line2 || ""}
              onChange={(value: string) => onChange(index, "line2", value)}
              error={getFieldErrorMessage(`address_${index}_line2`)}
            />
          </div>

          <div>
            <TextInputBox
              id={`address-${index}-line3`}
              label="Address Line 3"
              value={address.line3 || ""}
              onChange={(value: string) => onChange(index, "line3", value)}
              error={getFieldErrorMessage(`address_${index}_line3`)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <TextInputBox
                id={`address-${index}-city`}
                label="City"
                value={address.city || ""}
                onChange={(value: string) => onChange(index, "city", value)}
                error={getFieldErrorMessage(`address_${index}_city`)}
              />
            </div>

            <div>
              <TextInputBox
                id={`address-${index}-country`}
                label="Country"
                value={address.country || ""}
                onChange={(value: string) => onChange(index, "country", value)}
                error={getFieldErrorMessage(`address_${index}_country`)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <TextInputBox
                id={`address-${index}-zipCode`}
                label="Zip Code"
                value={address.zipCode || ""}
                onChange={(value: string) => onChange(index, "zipCode", value)}
                error={getFieldErrorMessage(`address_${index}_zipCode`)}
              />
            </div>

            <div>
              <TextInputBox
                id={`address-${index}-countryCode`}
                label="Country Code"
                value={address.countryCode || ""}
                onChange={(value: string) => onChange(index, "countryCode", value)}
                error={getFieldErrorMessage(`address_${index}_countryCode`)}
              />
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};

export default AddressItem;
